import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion'
import { Eye, EyeOff, Check, ArrowRight, MapPin, Search, Sparkles, ShieldCheck, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { useDispatch } from 'react-redux'

import { getLoginToken } from '@/redux/Auth/actions'
import { SET_USER_DETAIL } from '@/redux/Auth/constant'
import { sessionLogin } from '@/services/sessionClient'
import { setAuthToken } from '@/utility/CommonConstant'
import { useAuth } from '@/config/AuthContext'
import { getPostLoginPath } from '@/utils/agentUser'
import {
  TMS_BRAND,
  applyTmsDocumentTitle,
  applyAuthenticatedTmsDocumentTitle,
  persistTmsBrand,
} from '@/utils/tmsDocumentTitle'

const LIMCO_BG_VIDEO = `${process.env.PUBLIC_URL || ''}/limco-background.mp4`
const LIMCO_LOGO = `${process.env.PUBLIC_URL || ''}/LimcoLogo.png`
const LIMCO_LOGO_SMALL = `${process.env.PUBLIC_URL || ''}/limcoLogoSMALL.png`

/* Limco brand blue */
const BLUE_GRAD = 'linear-gradient(90deg, #3b82f6 0%, #2563eb 100%)'

const POINTERS = [
  { icon: MapPin, title: 'Real-time Tracking', desc: 'Live visibility across every shipment' },
  { icon: Search, title: 'Instant Rate Search', desc: 'Compare carrier rates in seconds' },
  { icon: Sparkles, title: 'AI-powered Workflows', desc: 'Automate the manual, scale faster' },
  { icon: ShieldCheck, title: 'Secure & Encrypted', desc: 'Enterprise-grade data protection' },
]

const PATHS = [
  'M350 40 C 210 40, 210 140, 72 140',
  'M350 106 C 210 106, 210 140, 72 140',
  'M350 172 C 210 172, 210 140, 72 140',
  'M350 238 C 210 238, 210 140, 72 140',
]

export default function LimcoLogin() {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { setIsAuthenticated } = useAuth()

  const [showPassword, setShowPassword] = useState(false)
  const [agree, setAgree] = useState(true)
  const [formData, setFormData] = useState({ email: '', password: '' })
  const [loading, setLoading] = useState(false)
  const [sessionConflict, setSessionConflict] = useState(null)
  const [conflictBusy, setConflictBusy] = useState(false)

  useEffect(() => {
    persistTmsBrand(TMS_BRAND.LIMCO)
    applyTmsDocumentTitle(TMS_BRAND.LIMCO)
  }, [])

  const cardMx = useMotionValue(0)
  const cardMy = useMotionValue(0)
  const cardRx = useSpring(useTransform(cardMy, [-0.5, 0.5], [8, -8]), { stiffness: 150, damping: 15 })
  const cardRy = useSpring(useTransform(cardMx, [-0.5, 0.5], [-8, 8]), { stiffness: 150, damping: 15 })
  const handleCardMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect()
    cardMx.set((e.clientX - r.left) / r.width - 0.5)
    cardMy.set((e.clientY - r.top) / r.height - 0.5)
  }
  const handleCardLeave = () => { cardMx.set(0); cardMy.set(0) }

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!agree) {
      toast.error('Please accept the User Agreement and Privacy Policy')
      return
    }
    if (!formData.email || !formData.password) {
      toast.error('Please enter both email and password')
      return
    }

    setLoading(true)
    const payload = JSON.stringify({
      username: formData.email,
      password: formData.password,
    })

    try {
      const authToken = await dispatch(getLoginToken(payload))
      if (authToken) {
        toast.success('Login successful!')
        persistTmsBrand(TMS_BRAND.LIMCO)
        applyAuthenticatedTmsDocumentTitle()
        setIsAuthenticated(true)
        navigate(getPostLoginPath())
      }
    } catch (error) {
      if (error?.sessionConflict) {
        setSessionConflict({
          conflict: error.sessionConflict,
          user: { email: formData.email, userID: null },
        })
        setLoading(false)
        return
      }
      const errorMessage =
        typeof error === 'string'
          ? error
          : error?.message || error?.response?.data?.detail || 'Login failed'
      toast.error(errorMessage)
    }
    setLoading(false)
  }

  const forceContinueHere = async () => {
    if (!sessionConflict) return
    setConflictBusy(true)
    try {
      let pendingUser = null
      let pendingToken = null
      let pendingResult = null
      try {
        const u = localStorage.getItem('shipez.pendingUser')
        const t = localStorage.getItem('shipez.pendingToken')
        const r = localStorage.getItem('shipez.pendingResult')
        if (u) pendingUser = JSON.parse(u)
        if (t) pendingToken = t
        if (r) pendingResult = JSON.parse(r)
      } catch (_) { /* ignore */ }

      if (!pendingUser || !pendingToken) {
        toast.error('Session data expired — please sign in again.')
        setConflictBusy(false)
        setSessionConflict(null)
        return
      }

      const res = await sessionLogin({ user: pendingUser, force: true })
      if (res?.success) {
        const authUserPayload = pendingResult || pendingUser
        const authUserDetail = Array.isArray(authUserPayload)
          ? authUserPayload[0]
          : authUserPayload

        setAuthToken(pendingToken, '', '', authUserPayload)
        dispatch({ type: SET_USER_DETAIL, payload: authUserDetail })
        try {
          localStorage.removeItem('shipez.pendingUser')
          localStorage.removeItem('shipez.pendingToken')
          localStorage.removeItem('shipez.pendingResult')
        } catch (_) { /* ignore */ }
        toast.success('Signed in — other device has been signed out.')
        setSessionConflict(null)
        persistTmsBrand(TMS_BRAND.LIMCO)
        applyAuthenticatedTmsDocumentTitle()
        setIsAuthenticated(true)
        navigate(getPostLoginPath())
      } else {
        toast.error('Could not take over the session. Please try again.')
      }
    } catch (err) {
      toast.error(`Failed to sign in. ${err?.message || ''}`)
    }
    setConflictBusy(false)
  }

  const cancelConflict = () => {
    setSessionConflict(null)
    try {
      localStorage.removeItem('authToken')
      localStorage.removeItem('authUser')
      localStorage.removeItem('shipez.session_id')
      localStorage.removeItem('shipez.pendingUser')
      localStorage.removeItem('shipez.pendingToken')
      localStorage.removeItem('shipez.pendingResult')
    } catch (_) { /* ignore */ }
    toast.info('Sign-in cancelled. The other device is still signed in.')
  }

  return (
    <div className="relative flex min-h-screen w-full items-center overflow-hidden text-slate-900">
      <video
        ref={(el) => { if (el) el.playbackRate = 0.5 }}
        className="absolute inset-0 -z-20 h-full w-full object-cover"
        src={LIMCO_BG_VIDEO}
        autoPlay
        loop
        muted
        playsInline
        aria-hidden
      />
      <div
        aria-hidden
        className="absolute inset-0 -z-10"
        style={{
          background:
            'linear-gradient(90deg, rgb(0 0 0 / 92%) 0%, rgb(0 0 0 / 82%) 45%, rgb(0 0 0 / 74%) 100%)',
        }}
      />

      <div className="relative z-10 mx-auto flex w-full max-w-[1400px] flex-col items-center gap-12 px-6 py-24 lg:flex-row lg:justify-between lg:px-14 lg:py-0">
        <motion.div
          initial={{ opacity: 0, x: -24 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="relative hidden select-none flex-col lg:-ml-6 lg:flex xl:-ml-12"
          style={{ marginTop: '-30px' }}
        >
        
          <h1
            className="whitespace-nowrap text-[54px] font-extrabold leading-[1.1] tracking-tight xl:text-[66px]"
            style={{ color: '#ffffff', textShadow: '0 2px 30px rgba(0,0,0,0.55)' }}
          >
            Digital Logistics System
          </h1>
          <p
            className="mt-4 max-w-[560px] text-[21px] font-medium leading-relaxed"
            style={{ color: 'rgba(255,255,255,0.82)' }}
          >
            Effectively monitor and control your entire logistics
          </p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="relative mt-9 h-[280px] w-full max-w-[560px]"
          >
            <div
              aria-hidden
              className="pointer-events-none absolute left-2 top-1/2 h-32 w-32 -translate-y-1/2 rounded-full blur-2xl"
              style={{ background: 'radial-gradient(circle, rgba(59,130,246,0.4), transparent 70%)' }}
            />

            <svg className="absolute inset-0 h-full w-full" viewBox="0 0 560 280" fill="none" preserveAspectRatio="none" aria-hidden>
              <defs>
                <linearGradient id="limco-flow-grad" x1="1" y1="0" x2="0" y2="0">
                  <stop offset="0%" stopColor="#bfdbfe" stopOpacity="0" />
                  <stop offset="45%" stopColor="#93c5fd" stopOpacity="1" />
                  <stop offset="70%" stopColor="#3b82f6" stopOpacity="1" />
                  <stop offset="100%" stopColor="#2563eb" stopOpacity="0" />
                </linearGradient>
              </defs>
              {PATHS.map((d, i) => (
                <path key={`base-${i}`} d={d} stroke="rgba(255,255,255,0.13)" strokeWidth="1.5" fill="none" />
              ))}
              {PATHS.map((d, i) => (
                <path
                  key={`flow-${i}`}
                  d={d}
                  className="limco-flow"
                  pathLength="1"
                  stroke="url(#limco-flow-grad)"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  fill="none"
                />
              ))}
            </svg>

            <div className="absolute left-[16px] top-1/2 -translate-y-1/2">
              <span aria-hidden className="limco-hub-pulse absolute inset-0 rounded-full" style={{ background: 'rgba(59,130,246,0.5)' }} />
              <span aria-hidden className="limco-hub-pulse absolute inset-0 rounded-full" style={{ background: 'rgba(59,130,246,0.35)', animationDelay: '1.1s' }} />
              <motion.span
                initial={{ opacity: 0, scale: 0.6 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.7, ease: [0.22, 1, 0.36, 1] }}
                className="relative flex h-14 w-14 items-center justify-center rounded-full p-2"
                style={{
                  background: BLUE_GRAD,
                  boxShadow: '0 16px 40px -8px rgba(37,99,235,0.9)',
                }}
              >
                <img
                  src={LIMCO_LOGO_SMALL}
                  alt=""
                  className="h-8 w-8 object-contain brightness-0 invert"
                  draggable={false}
                />
              </motion.span>
            </div>

            {POINTERS.map((p, i) => {
              const Icon = p.icon
              return (
                <motion.div
                  key={p.title}
                  initial={{ opacity: 0, x: 12 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.3 + i * 0.1, ease: [0.22, 1, 0.36, 1] }}
                  className="absolute left-[350px] flex w-[200px] items-center gap-3"
                  style={{ top: 16 + i * 66 }}
                >
                  <span
                    className="limco-badge flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-white/15 text-white"
                    style={{ background: 'rgba(255,255,255,0.06)', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}
                  >
                    <Icon className="h-5 w-5" strokeWidth={1.9} />
                  </span>
                  <span className="text-[12px] font-medium leading-tight text-white/75">{p.title}</span>
                </motion.div>
              )
            })}
          </motion.div>
        </motion.div>

        <div
          className="w-full max-w-[400px] [perspective:1100px]"
          onMouseMove={handleCardMove}
          onMouseLeave={handleCardLeave}
        >
          <motion.div
            initial={{ opacity: 0, y: 22, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.65, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            className="relative isolate w-full rounded-3xl border border-white/15 bg-white/[0.07] p-8 sm:p-9"
            style={{
              rotateX: cardRx,
              rotateY: cardRy,
              transformStyle: 'preserve-3d',
              WebkitBackdropFilter: 'blur(28px) saturate(160%)',
              backdropFilter: 'blur(28px) saturate(160%)',
              boxShadow:
                'inset 0 1px 0 rgba(255,255,255,0.20), inset 0 0 0 1px rgba(255,255,255,0.04), 0 40px 90px -30px rgba(0,0,0,0.75)',
            }}
          >
            <span aria-hidden className="limco-beam limco-beam-a pointer-events-none absolute inset-0 z-0 rounded-3xl" />
            <span aria-hidden className="limco-beam limco-beam-b pointer-events-none absolute inset-0 z-0 rounded-3xl" />

            <img
              src={LIMCO_LOGO}
              alt="Limco Logistics Inc."
              className="relative z-[1] mx-auto mb-4 h-12 w-auto object-contain lg:hidden"
              draggable={false}
            />
            <img
              src={LIMCO_LOGO}
              alt="Limco Logistics Inc."
              className="relative z-[1] mx-auto mb-4 hidden h-14 w-auto object-contain lg:block"
              draggable={false}
            />
            <h2 className="relative z-[1] text-left text-[21px] font-bold tracking-tight text-white">
              Sign in to Limco Logistics
            </h2>

            <form onSubmit={handleSubmit} className="relative z-[1] mt-6 flex flex-col gap-4">
              <input
                type="text"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Enter your email address"
                autoComplete="username"
                required
                className="h-[52px] w-full rounded-xl border border-white/10 bg-white/[0.05] px-4 text-[14px] text-white placeholder:text-white/40 outline-none transition-[border-color,box-shadow] duration-300 focus:border-blue-400/70 focus:bg-white/[0.08] focus:shadow-[0_0_0_4px_rgba(37,99,235,0.16)]"
              />

              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Enter your password"
                  autoComplete="current-password"
                  required
                  className="h-[52px] w-full rounded-xl border border-white/10 bg-white/[0.05] px-4 pr-12 text-[14px] text-white placeholder:text-white/40 outline-none transition-[border-color,box-shadow] duration-300 focus:border-blue-400/70 focus:bg-white/[0.08] focus:shadow-[0_0_0_4px_rgba(37,99,235,0.16)]"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 rounded-lg p-1.5 text-white/40 transition-colors hover:bg-white/10 hover:text-white"
                  tabIndex={-1}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>

              <label className="flex cursor-pointer select-none items-start gap-2.5 pt-0.5 text-[12.5px] leading-relaxed text-white/60">
                <button
                  type="button"
                  onClick={() => setAgree((v) => !v)}
                  aria-pressed={agree}
                  className={[
                    'mt-[1px] flex h-[17px] w-[17px] shrink-0 items-center justify-center rounded-[5px] border transition-all',
                    agree ? 'border-transparent text-white' : 'border-white/30 bg-white/5 hover:border-white/50',
                  ].join(' ')}
                  style={agree ? { background: BLUE_GRAD } : undefined}
                >
                  {agree && <Check className="h-3 w-3" strokeWidth={3.5} />}
                </button>
                <span>
                  I have read and agree to the{' '}
                  <a href="#" onClick={(e) => e.preventDefault()} className="font-semibold" style={{ color: '#60a5fa' }}>User Agreement</a>{' '}
                  and{' '}
                  <a href="#" onClick={(e) => e.preventDefault()} className="font-semibold" style={{ color: '#60a5fa' }}>Privacy Policy</a>
                </span>
              </label>

              <button
                type="submit"
                disabled={loading}
                className="group mt-1 inline-flex h-[52px] w-full items-center justify-center gap-2 rounded-xl text-[15px] font-semibold text-white transition-all duration-300 hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-70"
                style={{ background: BLUE_GRAD, boxShadow: '0 16px 34px -14px rgba(37,99,235,0.6)' }}
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Signing in…
                  </>
                ) : (
                  <>
                    Log In
                    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
                  </>
                )}
              </button>
            </form>
          </motion.div>
        </div>
      </div>

      {sessionConflict && createPortal(
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4 overflow-y-auto"
          style={{ background: 'rgba(0,4,20,0.86)', backdropFilter: 'blur(8px)' }}
        >
          <div
            className="my-auto w-full max-w-lg rounded-2xl overflow-hidden shadow-2xl"
            style={{ background: '#0b1830', border: '1px solid #2563eb' }}
            data-testid="session-conflict-modal"
          >
            <div
              className="px-6 py-4 flex items-center gap-3"
              style={{ borderBottom: '1px solid #1e293b', background: 'linear-gradient(90deg, rgba(37,99,235,0.18), transparent)' }}
            >
              <div
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full p-1.5"
                style={{ background: BLUE_GRAD, boxShadow: '0 8px 24px -8px rgba(37,99,235,0.85)' }}
              >
                <img
                  src={LIMCO_LOGO_SMALL}
                  alt="Limco"
                  className="h-6 w-6 object-contain brightness-0 invert"
                  draggable={false}
                />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">
                  Same user is signed in on another device
                </h3>
                <p className="text-xs mt-0.5 text-slate-400">
                  Only one active session is allowed per user.
                </p>
              </div>
            </div>

            <div className="max-h-[min(70vh,520px)] overflow-y-auto px-6 py-4 space-y-3 text-sm text-slate-300">
              <p>
                <strong className="text-white">{formData.email}</strong> is currently signed in on
                another computer. If you continue here, that device will be signed out automatically.
              </p>

              {(sessionConflict.conflict?.active_session || sessionConflict.conflict?.conflict?.active_session) && (
                <div
                  className="rounded-lg p-3 space-y-1.5"
                  style={{ background: 'rgba(15,23,42,0.6)', border: '1px solid #334155' }}
                >
                  {(() => {
                    const active =
                      sessionConflict.conflict?.active_session ||
                      sessionConflict.conflict?.conflict?.active_session
                    return (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                        <div>
                          <div className="text-slate-500">Device</div>
                          <div className="text-slate-200">{active.device_name || '—'}</div>
                        </div>
                        <div>
                          <div className="text-slate-500">Browser / OS</div>
                          <div className="text-slate-200">
                            {active.browser || '—'}
                            {active.os ? (
                              <span className="text-slate-400"> · {active.os}</span>
                            ) : null}
                          </div>
                        </div>
                        <div>
                          <div className="text-slate-500">IP</div>
                          <div className="text-slate-200">
                            {active.ip_address || '—'}
                            {active.local_ip ? (
                              <span className="text-slate-400"> (LAN {active.local_ip})</span>
                            ) : null}
                          </div>
                        </div>
                        <div>
                          <div className="text-slate-500">Location</div>
                          <div className="text-slate-200">
                            {active.city || '—'}
                            {active.country ? (
                              <span className="text-slate-400">, {active.country}</span>
                            ) : null}
                          </div>
                        </div>
                        <div className="sm:col-span-2">
                          <div className="text-slate-500">Signed in</div>
                          <div className="text-slate-200">
                            {active.created_at
                              ? new Date(active.created_at).toLocaleString()
                              : '—'}
                          </div>
                        </div>
                      </div>
                    )
                  })()}
                </div>
              )}

              <p className="text-xs text-slate-400">
                Tip: if this wasn&apos;t you, please review the login history under System
                Maintenance and change your password.
              </p>
            </div>

            <div
              className="px-6 py-4 flex flex-wrap items-center justify-end gap-2"
              style={{ borderTop: '1px solid #1e293b', background: 'rgba(15,23,42,0.4)' }}
            >
              <button
                type="button"
                onClick={cancelConflict}
                disabled={conflictBusy}
                data-testid="session-cancel-btn"
                className="px-4 py-2 rounded-lg text-sm bg-slate-800 text-slate-300"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={forceContinueHere}
                disabled={conflictBusy}
                data-testid="session-force-btn"
                className="px-4 py-2 rounded-lg text-sm font-medium inline-flex items-center gap-2 text-white disabled:opacity-70"
                style={{ background: BLUE_GRAD }}
              >
                {conflictBusy ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Signing in…
                  </>
                ) : (
                  'Sign out other device & continue'
                )}
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}

      <style>{`
        @property --limco-a { syntax: '<angle>'; initial-value: 0deg; inherits: false; }
        @property --limco-b { syntax: '<angle>'; initial-value: 0deg; inherits: false; }
        .limco-beam {
          padding: 2px;
          -webkit-mask:
            linear-gradient(#000 0 0) content-box,
            linear-gradient(#000 0 0);
          -webkit-mask-composite: xor;
                  mask-composite: exclude;
          filter: drop-shadow(0 0 4px rgba(59,130,246,0.65));
        }
        .limco-beam-a {
          background-image: conic-gradient(from calc(315deg + var(--limco-a)),
            #ffffff 0deg, #60a5fa 8deg, #3b82f6 22deg,
            transparent 72deg, transparent 288deg,
            #3b82f6 338deg, #60a5fa 356deg, #ffffff 360deg);
          animation: limco-a-spin 6s linear infinite;
        }
        .limco-beam-b {
          background-image: conic-gradient(from calc(135deg - var(--limco-b)),
            #ffffff 0deg, #60a5fa 8deg, #3b82f6 22deg,
            transparent 72deg, transparent 288deg,
            #3b82f6 338deg, #60a5fa 356deg, #ffffff 360deg);
          animation: limco-b-spin 6s linear infinite;
        }
        @keyframes limco-a-spin { to { --limco-a: 360deg; } }
        @keyframes limco-b-spin { to { --limco-b: 360deg; } }
        .limco-flow {
          stroke-dasharray: 0.32 0.68;
          filter: drop-shadow(0 0 3px rgba(96,165,250,0.85));
          animation: limco-flow-move 3s linear infinite;
        }
        @keyframes limco-flow-move {
          from { stroke-dashoffset: 1; }
          to   { stroke-dashoffset: 0; }
        }
        .limco-hub-pulse {
          animation: limco-hub-ping 2.4s cubic-bezier(0, 0, 0.2, 1) infinite;
        }
        @keyframes limco-hub-ping {
          0%   { transform: scale(1);   opacity: 0.55; }
          70%  { transform: scale(1.9); opacity: 0; }
          100% { transform: scale(1.9); opacity: 0; }
        }
        .limco-badge { transition: transform .3s ease, border-color .3s ease, box-shadow .3s ease; }
        .limco-badge:hover {
          transform: translateY(-3px);
          border-color: rgba(96,165,250,0.6);
          box-shadow: 0 10px 26px -10px rgba(37,99,235,0.7);
        }
        @media (prefers-reduced-motion: reduce) {
          .limco-beam-a, .limco-beam-b, .limco-flow, .limco-hub-pulse { animation: none; }
          .limco-badge { transition: none; }
        }
      `}</style>
    </div>
  )
}
